import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import { Action, Store } from '@ngrx/store';
import { produce } from 'immer';
import { first, map, Observable, of } from 'rxjs';

import {
  ApplyWorkflowNodePayload,
  runtimeActions,
  runtimeSelectors,
  WebSocketConfig,
} from '@blaze-runtime/engine-data-access';
import { CanvasElement, RuntimeErrorMessage, RuntimeEvent } from '@blaze-runtime/engine-domain';
import { assertNextNodeIsNotSelf } from '@blaze-runtime/workflow-domain';
import { DialogActionService, throwInvalidNodeError } from '@blaze-runtime/workflow-feature';
import {
  ColumnId,
  ComparisonOperator,
  ComparisonQueryNode,
  ExecuteWorkflowResponse,
  LogicalQueryNode,
  ServerErrorResponse,
  VariableInfo,
  WorkflowNodeType,
  WorkflowPredicate,
  WorkflowVariableInfo,
} from '@blaze-shared/api-contracts-domain';
import { getAllComparisonNodes, Pagination } from '@blaze-shared/datasets-domain';
import {
  ElementPaginationRequest,
  ElementQueryState,
  getActiveBinding,
  GlobalQueryState,
  PaginationDirection,
  RuntimeContext,
} from '@blaze-shared/elements-domain';
import { IntelliSenseService } from '@blaze-shared/intellisense-feature';
import {
  isHttpErrorResponse,
  NotificationService,
  syncSelect,
  WorkflowLoggerService,
} from '@blaze-shared/platform-data-access';
import { PageContext } from '@blaze-shared/projects-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  ApiCallSchema,
  BlazeSchema,
  convertBlazeSchemaToWorkflowSchema,
  convertDatasetSchemaToWorkflowSchema,
  convertPageContextSchemaToWorkflowSchema,
  convertStoredVariableSchemaToWorkflowSchema,
  DatasetSchema,
  DEFAULT_WF_SCHEMA,
  findColumn,
  getColumnKey,
  isActionEventType,
  isDatasetLinkValueLike,
  isTagId,
  WorkflowSchema,
} from '@blaze-shared/schema-domain';
import { Any, areAllValuesDefined } from '@blaze-shared/shared-util';
import { deepClone, isNullish, type Nullish } from '@blaze-shared/shared-util';
import { VariablesFacade } from '@blaze-shared/variables-data-access';
import { normalizeVarName } from '@blaze-shared/variables-util';
import {
  BULK_EXECUTE_NODE_ID,
  Connection,
  DataAssignment,
  findFirstNodeInWorkflow,
  getEffectiveWorkflowId,
  getWorkflowNodeById,
  hasBulkEditPrompt,
  hasLogicalQueryNode,
  hasPrompt,
  hasQueryConditions,
  isApiCallNode,
  isAssignNode,
  isBulkActionWorkflow,
  isExecuteWorkflowNode,
  isFrontendOnlyNode,
  isGetNode,
  isIfNode,
  isInsertNode,
  isNodeWithPrompt,
  MAIN_INPUT_NAME,
  RuntimeElement,
  Workflow,
  WorkflowInputs,
  WorkflowNode,
  WorkflowPhaseInfo,
} from '@blaze-shared/workflow-domain';

import { fromErrors } from '..';
import { hasDataProps } from '../../canvas-elements/element-model/element-props-guards';
import {
  isElementType,
  isVariantOfButton,
} from '../../canvas-elements/element-model/element-type-guards';
import { BulkEditService } from '../../services/bulk-edit.service';
import { ElementDefinitionsService } from '../../services/element-definitions.service';
import {
  DEFAULT_PAGE_SIZE,
  ElementPaginationService,
} from '../../services/element-pagination.service';
import { RuntimeService } from '../../services/runtime.service';
import { WorkflowReferenceService } from '../../services/workflow-reference.service';
import { getWorkflowRuntimeElement } from '../../utils/runtime/runtime';

import { RuntimeFacade } from './runtime.facade';

const RUN_LOCALLY = false;

export type NodePromptOutput = {
  cancelledByDialog?: boolean;
  dialogOutputData: Record<string, any> | null | undefined;
  nodeInput: Record<string, any> | null | undefined;
};

export interface PaginationConfigData {
  globalQuery: GlobalQueryState | undefined;
  paginationRequests: Dictionary<ElementPaginationRequest>;
  paginations: Dictionary<ElementQueryState>;
  paginationTarget: RuntimeElement | undefined;
  returnTotalCount: boolean;
  uiEntities: Dictionary<CanvasElement>;
  workflow: Workflow;
}

export interface ServerExecuteData {
  ctx: RuntimeContext;
  event: RuntimeEvent;
  execResponse: ExecuteWorkflowResponse;
  isResumedExecution?: boolean;
  nodeInput: any;
  pagination: Pagination | null;
  schema: WorkflowSchema;
  socketConfig?: WebSocketConfig;
  target?: RuntimeElement | undefined;
  workflow: Workflow;
  workflowExecutionId: string;
  workflowNode: WorkflowNode | null;
}

export interface ServerExecuteErrorData {
  ctx: RuntimeContext;
  error: HttpErrorResponse | ServerErrorResponse;
  event: RuntimeEvent;
  schema: WorkflowSchema;
  target: RuntimeElement | undefined;
  workflow: Workflow;
  workflowExecutionId: string;
  workflowNode: WorkflowNode | null;
}

export interface WorkflowBlockedReasonParams {
  event: RuntimeEvent;
  workflow: Workflow | null;
  ctx: RuntimeContext;
  uiEntities: Dictionary<CanvasElement>;
  connections: Connection[];
  target: RuntimeElement | undefined;
  paginationRequests: Dictionary<ElementPaginationRequest>;
  workflowExecutionId?: string;
}

@Injectable({ providedIn: 'root' })
export class WorkflowEffectsService {
  private readonly bulkEditService = inject(BulkEditService);
  private readonly dialogActionService = inject(DialogActionService);
  private readonly elementDefinitionsService = inject(ElementDefinitionsService);
  private readonly elementPaginationService = inject(ElementPaginationService);
  private readonly iss = inject(IntelliSenseService);
  private readonly logger = inject(WorkflowLoggerService);
  private readonly notificationService = inject(NotificationService);
  private readonly runtimeFacade = inject(RuntimeFacade);
  private readonly runtimeService = inject(RuntimeService);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store<any>);
  private readonly variablesFacade = inject(VariablesFacade);
  private readonly workflowReferenceService = inject(WorkflowReferenceService);

  applyNextWorkflowNode(nodePayload: ApplyWorkflowNodePayload): Action {
    const { workflow, workflowExecutionId } = nodePayload;
    let forceCancelWorkflow = false;

    // Check whether a page-scoped workflow should be cancelled because we're no longer in the proper scope.
    this.store
      .select(runtimeSelectors.selectPageId)
      .pipe(first())
      .subscribe((pageId) => {
        if (workflow?.isPageScoped && workflow.pageId !== pageId) {
          this.logger.log(`Cancelling page-scoped workflow ${workflow.id}`, {
            extra: {
              pageId,
              workflowPageId: workflow.pageId,
            },
            workflowExecutionId,
          });
          forceCancelWorkflow = true;
          nodePayload = { ...nodePayload, workflowNode: null };
        }
      });

    return this.canWorkflowBeResumed(nodePayload, forceCancelWorkflow)
      ? runtimeActions.resumeWorkflowNode(nodePayload)
      : runtimeActions.applyWorkflowNode(nodePayload);
  }

  cancelNextWorkflowNode(
    nodePayload: ApplyWorkflowNodePayload,
    nodeInput: any,
    prevNodeId?: string,
  ): Action {
    return this.applyNextWorkflowNode({
      ...nodePayload,
      isWorkflowCancelled: true,
      nodeInput,
      prevNodeId,
      userInputs: null,
      workflowNode: null,
    });
  }

  /**
   * PageContext can override input1 only if input1 is not set and the source layer for the connection
   * is 'Data'. This is done to avoid overwriting connections from UI elements to workflows which
   * provide their own data.
   * @returns - true if PageContext can overwrite input1 data.
   */
  canPageContextOverwriteInput1(
    connection: Connection,
    inputsData: any,
    pageContext?: PageContext,
  ): boolean {
    return !!(
      !inputsData?.[MAIN_INPUT_NAME] &&
      connection?.source?.layerType === 'Data' &&
      pageContext?.data
    );
  }

  canWorkflowBeResumed(nodePayload: ApplyWorkflowNodePayload, forceCancel = false): boolean {
    return !nodePayload?.isWorkflowCancelled && !forceCancel && !!nodePayload?.resumeToken;
  }

  createPaginationForServer(paginationConfig: PaginationConfigData): {
    pagination: Pagination | null;
    query: LogicalQueryNode | null;
    groups: (ColumnId | string)[] | null;
  } {
    const {
      globalQuery,
      paginationRequests,
      paginations,
      paginationTarget,
      returnTotalCount,
      uiEntities,
      workflow,
    } = paginationConfig;
    const isBulkActionWf = isBulkActionWorkflow(workflow);

    if (!paginationTarget?.id) {
      return { pagination: null, query: null, groups: null };
    }
    if (paginationTarget.layerType !== 'Ui' && !isBulkActionWf) {
      return { pagination: null, query: null, groups: null };
    }

    const entity = uiEntities[paginationTarget.id];
    const elementPaginationState = paginations[paginationTarget.id];
    const paginationRequest = paginationRequests[paginationTarget.id];
    const paginationProps = this.elementPaginationService.getPaginationProps(entity);
    const groups = this.getGroupsForElement(uiEntities[paginationTarget.id]);

    // If the input is not queryable, don't pass pagination, query or grouping data for it.
    // Exclude bulk actions as they have their own pagination and query handling.
    if (!isBulkActionWf && hasDataProps(entity)) {
      const queryableInput = this.elementDefinitionsService.getQueryableInput(entity.type);

      const binding = getActiveBinding(entity.props?.data, queryableInput?.name);

      if (
        binding?.dataSource?.sourceType !== 'context' ||
        binding?.dataSource?.id !== workflow.id
      ) {
        return { pagination: null, query: null, groups: null };
      }
    }

    if (!isBulkActionWf && ((!entity && !elementPaginationState) || !paginationProps?.enabled)) {
      const query = this.mergeQueries(elementPaginationState?.query, globalQuery?.query);
      return { pagination: null, query, groups };
    }

    const pagination: Pagination = {};

    if (returnTotalCount) {
      pagination.returnTotalCount = true;
    }

    if (
      elementPaginationState?.sort ||
      (paginationProps?.sortColumnName && paginationProps.sortDirection)
    ) {
      const sort = elementPaginationState?.sort || {
        columnName: paginationProps?.sortColumnName,
        direction: paginationProps?.sortDirection,
      };

      // Check if column used for default sorting exists only when schema is present
      const schema = this.schemaService.getWorkflowSchema(workflow.id);
      if (schema) {
        const sortColumnExist = findColumn(schema.columns, sort.columnName);
        pagination.sort = sortColumnExist ? sort : undefined;
      } else {
        pagination.sort = sort;
      }
    }

    if (isBulkActionWf) {
      const query = this.mergeQueries(elementPaginationState?.query, globalQuery?.query);
      return { pagination, query, groups: null };
    }
    if (!paginationProps) {
      // sanity check (the if above & the one before pagination declaration, prevent this)
      return { pagination: null, query: null, groups };
    }

    pagination.maxCount =
      (paginationProps.pageSize || elementPaginationState?.pageSize) ?? DEFAULT_PAGE_SIZE;
    if (pagination.maxCount === 0) {
      pagination.maxCount = undefined;
    }

    const currentPageIndex = elementPaginationState?.pageIndex || 0;
    let pageToken;

    if (paginationRequest) {
      switch (paginationRequest?.direction) {
        case PaginationDirection.$Initial:
        case PaginationDirection.First:
          pageToken = '';
          break;
        case PaginationDirection.Forwards:
          pageToken = elementPaginationState?.nextPageToken || '';
          break;
        case PaginationDirection.Backwards:
          // When trying to get first page token should be empty
          pageToken = (currentPageIndex > 0 && elementPaginationState?.previousPageToken) || '';
          break;
      }
    } else {
      pageToken = elementPaginationState?.currentPageToken || '';
    }

    pagination.pageToken = pageToken;
    const query = this.mergeQueries(elementPaginationState?.query, globalQuery?.query);
    return { pagination, query, groups };
  }

  /**
   * Creates final workflow output data and schema objects.
   */
  createWorkflowOutputData(
    workflow: Workflow | null,
    ctx: RuntimeContext,
    lastNodeOutputData: any,
    outputSchema: WorkflowSchema,
  ): { outputData: any; outputSchema: WorkflowSchema } {
    // Workflow outputs workflow output data
    if (!workflow?.output?.startsWith('@@')) {
      return { outputData: lastNodeOutputData, outputSchema };
    }

    // Workflow outputs a variable
    const varKey = normalizeVarName(workflow.output);
    const outputData = [{ [workflow.output]: ctx.storedVars[varKey] }];
    const varSchemaColumn = ctx.storedVarsFullInfo[varKey]?.column;
    // Use schema returned by the server if exists. If not, deduce it from data.
    if (varSchemaColumn) {
      outputSchema = convertStoredVariableSchemaToWorkflowSchema(outputSchema, varSchemaColumn);
    } else {
      outputSchema = this.iss.getWorkflowSchemaFromData(outputSchema, outputData);
    }

    return { outputData, outputSchema };
  }

  /**
   * Formats data to be accepted by the server in resume call as userInputs.
   * @param data
   * @param removeIds - Sometimes ids must be returned (for example when doing an insert);
   * @returns -
   */
  formatUserInput(
    data: Record<string, Any> | Record<string, Any>[] | null | undefined,
    schema: BlazeSchema,
    removeIds = false,
  ): Record<string, Any> | Record<string, Any>[] | null {
    if (!data) {
      return null;
    }
    const userInput = Array.isArray(data) ? data[0] : data;
    if (removeIds) {
      const idColumnName = this.schemaService.getIdColumnName(schema.name, schema);
      if (idColumnName) {
        delete userInput[idColumnName];
      }
    }
    return userInput ?? null;
  }

  getContextData(workflow: Workflow | null, nodeInput: any): any {
    const startNode = workflow?.startNodeId
      ? workflow.nodes.find(({ id }) => id === workflow.startNodeId)
      : undefined;
    const isBulkAction = !startNode
      ? false
      : isExecuteWorkflowNode(startNode) && startNode.id === BULK_EXECUTE_NODE_ID;
    return Array.isArray(nodeInput) && !isBulkAction ? nodeInput[0] : nodeInput;
  }

  getDataForWorkflowInputs(workflow: Workflow | undefined): { [key: string]: any } {
    const inputs = this.getWorkflowInputs(workflow);
    const data: { [key: string]: any } = {};
    if (!inputs) {
      return data;
    }
    for (const inputName in inputs) {
      const connection = this.runtimeFacade.getConnectionById(inputs[inputName]);
      if (connection?.source) {
        data[inputName] = this.runtimeService.getDataForElement(
          connection.source,
          connection.eventType,
        );
      }
    }
    return data;
  }

  getFlatGroupedData(
    groupedData: Record<string, any>[],
    level = 0,
    prevLevelId?: string,
  ): Record<string, any>[] {
    const result = [];

    for (let i = 0; i < groupedData.length; i++) {
      const group = groupedData[i];
      let groupName = (group.name2 ?? group.name) || '(empty)';
      // If the group name is a link or link array, we need to extract the display value
      if (Array.isArray(groupName)) {
        if (groupName.every(isDatasetLinkValueLike)) {
          groupName = groupName.map((link) => link.display).join(', ');
        }
      } else if (isDatasetLinkValueLike(groupName)) {
        groupName = groupName.display;
      }
      const record = {
        __groupBy: groupName,
        ...group.values,
        __level: level,
        __id: `${prevLevelId ? prevLevelId + '_' : ''}${groupName}_${level}_${i}`,
        __groupCount: getGroupDataRowCount(group),
      };
      result.push(record);

      result.push(...group.data);
      if ('groupedData' in group && group.groupedData.length) {
        result.push(...this.getFlatGroupedData(group.groupedData, level + 1, record.__id));
      }
    }

    return result;
  }

  /**
   * Finds and returns a global query (global data filter component query) that should affect specified workflow.
   */
  getGlobalQueryForWorkflow(workflow: Workflow): Observable<GlobalQueryState | undefined> {
    return this.store.select(runtimeSelectors.selectGlobalQuery).pipe(
      first(),
      map((globalQueries) => {
        return this.findGlobalQueryForWorkflow(workflow, globalQueries);
      }),
    );
  }

  getRuntimeErrorActions(
    error: any,
    event: RuntimeEvent,
    workflowId?: string,
    origin?: WorkflowNodeType | string,
    nodeId?: string,
  ): Action[] {
    if (!workflowId) {
      return [];
    }
    return [
      fromErrors.runtimeError({
        errorMessage:
          error?.['error']?.['error']?.['errorMessage'] ||
          error?.['error']?.['errorMessage'] ||
          error?.['message'],
        event,
        nodeId,
        ns: event.ns,
        origin: origin,
        workflowId: workflowId,
      }),
    ];
  }

  getWorkflowInput1Schema(
    workflow: Workflow,
    event: RuntimeEvent,
    inputsData: { [key: string]: any },
    pageContext: PageContext | undefined,
  ): WorkflowSchema {
    const defaultSchema: WorkflowSchema = { ...DEFAULT_WF_SCHEMA, workflowId: workflow.id };

    // PageContext wins when input1 is unset and the connection source is Data (not a UI element).
    if (this.canPageContextOverwriteInput1(event.connection, inputsData, pageContext)) {
      return pageContext?.schema
        ? convertPageContextSchemaToWorkflowSchema(pageContext.schema)
        : defaultSchema;
    }

    const firstNode = findFirstNodeInWorkflow(workflow);
    // Dataset schema fallback when the first node is a Get node (e.g. ApiCall without known schema).
    // Passed separately so Compute does not mutate the same schema more than once.
    let firstGetNodeSchema: WorkflowSchema | null = null;

    if (this.runtimeService.isAutostartableNode(firstNode) && isGetNode(firstNode)) {
      const datasetSchema = this.schemaService.getSchema<DatasetSchema>(firstNode.datasetId || '');
      firstGetNodeSchema = datasetSchema
        ? convertDatasetSchemaToWorkflowSchema(datasetSchema, '')
        : null;
    }

    const eventSchema = convertBlazeSchemaToWorkflowSchema(event.schema, workflow.id);

    const formWfInputSchema = this.schemaService.getMainInputSchema(workflow);
    const inputSchema = eventSchema ?? firstGetNodeSchema ?? formWfInputSchema ?? defaultSchema;

    const hasInputDataWithoutSchema =
      this.runtimeService.isAutostartableNode(firstNode) &&
      !inputSchema.columns?.length &&
      !!inputsData?.[MAIN_INPUT_NAME];

    // Data from another page (e.g. Print Invoice) may exist without a schema.
    if (!hasInputDataWithoutSchema) {
      return inputSchema;
    }

    return this.iss.getWorkflowSchemaFromData(DEFAULT_WF_SCHEMA, inputsData[MAIN_INPUT_NAME]);
  }

  /**
   * Following workflows are blocked from execution:
   * - automatic workflows to which buttons are connected
   * - workflows whose event source is not one of the ui entities found
   */
  getWorkflowBlockedReason(params: WorkflowBlockedReasonParams): WorkflowPhaseInfo | false {
    const {
      event,
      ctx,
      workflowExecutionId,
      workflow,
      target,
      uiEntities,
      paginationRequests,
      connections,
    } = params;
    const source = event.connection.source;
    const nodeInputs = workflow?.inputs || {};
    const uiEntityIds = Object.keys(uiEntities);

    const isAutostart = !!event.connection.autostart;
    const isDataRefresh = !!event.connection.dataRefresh;
    const ignorePreventAutostart = !!event.connection.ignorePreventAutostart;
    const isDataRefreshOfUninitializedTarget =
      isDataRefresh &&
      target?.layerType === 'Ui' &&
      !this.runtimeService.isElementInitialized(target.id);

    // File upload button
    const fileButtonConnection = connections.find((c) => {
      return (
        c.source.layerType === 'Ui' &&
        c.target.id === workflow?.id &&
        c.target.layerType === 'Workflow' &&
        (uiEntities[c.source.id]?.type === 'uploadFile' ||
          uiEntities[c.source.id]?.type === 'audioRecorder')
      );
    });
    const hasAttachedFileButton = !!fileButtonConnection;
    const isFileChangeEvent = hasAttachedFileButton && event.connection.eventType === 'change';
    let fileButtonInputName = '';
    if (hasAttachedFileButton) {
      for (const inputName in workflow?.inputs || {}) {
        // We're interested only in named inputs -- input1 name is handled separately.
        if (
          inputName !== MAIN_INPUT_NAME &&
          workflow?.inputs[inputName] === fileButtonConnection.id
        ) {
          fileButtonInputName = inputName;
        }
      }
    }

    const isClickOrActionEvent =
      event.connection.eventType === 'click' ||
      isActionEventType(event.connection.eventType) ||
      // upload-file changes, when the input is connected to a workflow, should be triggered by user interaction
      (event.eventContext?.userUpdate && isFileChangeEvent);

    const missingDataInputs = this.getMissingDataInputs(event, workflow, ctx, fileButtonInputName);
    const missingExternalVars = this.findMissingExternalVars(workflow, ctx);
    const hasSourceEntity = uiEntityIds.find((entityId) =>
      source.layerType === 'Data' ? true : entityId === source.id,
    );
    const targetPaginationRequest = target ? paginationRequests[target.id] : undefined;
    const paginationNavigationUserActions = [
      PaginationDirection.First,
      PaginationDirection.Backwards,
      PaginationDirection.Forwards,
    ];
    const isTargetPaginationNavigationPending = targetPaginationRequest?.direction
      ? paginationNavigationUserActions.includes(targetPaginationRequest.direction)
      : false;
    let hasAttachedButton = !!connections.find(
      (c) =>
        c.target.layerType === 'Workflow' &&
        c.target.id === workflow?.id &&
        c.source.layerType === 'Ui' &&
        isVariantOfButton(uiEntities[c.source.id]),
    );

    const shouldBlockWizardEvent = shouldBlockWizardEventWorkflow(
      workflow?.id,
      uiEntities,
      connections,
      event.connection,
      isAutostart,
      isDataRefresh,
    ); // Wizard submit workflow & submit event & autostart/data refresh -> block

    if (!hasAttachedButton) {
      for (const inputName in nodeInputs) {
        if (Object.prototype.hasOwnProperty.call(nodeInputs, inputName)) {
          const connectionId = nodeInputs[inputName] || '';
          const connection = this.runtimeFacade.getConnectionById(connectionId);
          const connSource = connection?.source?.layerType === 'Ui' ? connection.source : null;
          if (connSource) {
            const uiEntity = uiEntities[connSource.id];
            if (isVariantOfButton(uiEntity)) {
              hasAttachedButton = true;
            }
          }
        }
      }
    }

    // Upload button functions in two states: as a value holder (such as any other input), and as a button which can
    // trigger a workflow. Because of this, we need to make sure that when datasets are updated and refresh dataset
    // event is triggered, we don't trigger the refresh on workflows which have upload button or record audio button
    // attached (so that we don't lose uploaded file information)
    //
    // A data refresh of a never-initialized target (e.g. a never-selected Tabs slot) is de facto
    // an auto-start, so preventAutoStart blocks it; initialized targets must keep refreshing.
    const isDataRefreshWithFileButton = hasAttachedFileButton && isDataRefresh;
    const isAutoStartPreventedUninitializedRefresh =
      isDataRefreshOfUninitializedTarget &&
      !!workflow?.preventAutoStart &&
      !ignorePreventAutostart &&
      !isTargetPaginationNavigationPending;

    if (isDataRefreshWithFileButton || isAutoStartPreventedUninitializedRefresh) {
      return WorkflowPhaseInfo.BlockedDataRefreshEvent;
    }

    // Block workflow when external vars used in the workflow are missing.
    if (missingExternalVars.length) {
      this.logger.log(
        `Blocked workflow ${workflow?.id} due to missing external vars: ${missingExternalVars.join(', ')}`,
        {
          extra: { missingExternalVars },
          level: 'warning',
          logToSentry: true,
          workflowExecutionId,
          workflowId: workflow?.id || '',
        },
      );
      return WorkflowPhaseInfo.BlockedMissingRequiredExternalVars;
    }

    // This can happen if we open a dialog but a workflow on the opening page tries to run
    if (!hasSourceEntity) {
      return WorkflowPhaseInfo.BlockedMissingSourceEntity;
    }
    // Not all data from required inputs have been found
    else if (missingDataInputs.length) {
      this.logger.log(`Missing data inputs in workflow ${workflow?.id}:`, {
        extra: {
          missingDataInputs,
        },
        level: 'error',
        workflowExecutionId: workflowExecutionId,
        workflowId: workflow?.id || '',
      });
      return WorkflowPhaseInfo.BlockedMissingInputs;
    } else if ((isAutostart || isDataRefresh) && (isClickOrActionEvent || isFileChangeEvent)) {
      return WorkflowPhaseInfo.BlockedAutostartClickEvent;
    }
    // Some workflows are explicitly prevented from being auto-started.
    // Forward or backward pagination navigation should not be prevented from auto-starting.
    else if (
      isAutostart &&
      workflow?.preventAutoStart &&
      !ignorePreventAutostart &&
      !isTargetPaginationNavigationPending
    ) {
      return WorkflowPhaseInfo.BlockedPreventAutostartProperty;
    } else if (
      !isTargetPaginationNavigationPending &&
      (((isAutostart || isDataRefresh) && hasAttachedButton) ||
        (!(isAutostart || isDataRefresh) &&
          !isClickOrActionEvent &&
          (hasAttachedButton || hasAttachedFileButton)) ||
        shouldBlockWizardEvent)
    ) {
      return WorkflowPhaseInfo.BlockedAttachedButtons;
    }
    return false;
  }

  getWorkflowTargetElement(workflow: Workflow, connection: Connection): RuntimeElement | undefined {
    return !isBulkActionWorkflow(workflow)
      ? this.getNextConnection(connection)?.target
      : ({ id: connection.source.id, layerType: 'Void' } as RuntimeElement);
  }

  handleServerExecution(serverExecute: ServerExecuteData): Action[] {
    let { ctx, nodeInput, schema } = serverExecute;
    const {
      event,
      execResponse,
      isResumedExecution,
      pagination,
      socketConfig,
      target,
      workflow,
      workflowExecutionId,
      workflowNode,
    } = serverExecute;

    const execSchema: WorkflowSchema = {
      ...(execResponse?.schema || DEFAULT_WF_SCHEMA),
      columnOriginNodes: {},
      id: workflow.id,
      name: execResponse.name || '',
      workflowId: workflow.id,
      workflowRevision: workflow.revision,
    };
    const execPagination = execResponse.pagination;
    this.runtimeService.setWorkflowPagination(workflow.id, execPagination);
    const isPaginated = this.runtimeService.isPaginated(workflow.id);

    const actions: Action[] = [];

    // If it's the initial execution call (not resumed) and it's not a bulk action execution
    // Store pagination tokens for the target element.
    if (!isResumedExecution && target?.layerType !== 'Void') {
      actions.push(
        runtimeActions.storeElementPaginationTokens({
          elementId: target?.id,
          paginationRequest: pagination,
          paginationResponse: execPagination,
        }),
      );
    }

    if (
      execResponse.createdSubscriptionIds?.length &&
      socketConfig?.channelId &&
      socketConfig?.websocket
    ) {
      actions.push(
        runtimeActions.subscribeToWebSocketWfRefresh({
          subscriptionIds: execResponse.createdSubscriptionIds,
          socketConfig,
        }),
      );
    }

    if (execResponse?.error) {
      this.logger.log(execResponse.error, {
        level: 'error',
        workflowExecutionId,
      });
    }
    if (execResponse?.nextNodeId === workflow.startNodeId) {
      this.logger.log(
        `Workflow ${workflow?.id} was unable to fully execute remotely. Running locally.`,
        {
          workflowExecutionId,
        },
      );
      schema = schema || { ...DEFAULT_WF_SCHEMA, workflowId: workflow.id };
      ctx = this.iss.updateRuntimeContextNamedOutput(ctx, nodeInput, execResponse.outputName);

      const nextNodePayload: ApplyWorkflowNodePayload = {
        affectedDatasetIds: execResponse?.affectedDatasetIds,
        ctx,
        event,
        nodeInput: nodeInput,
        outputName: execResponse.outputName,
        prevNodeId: execResponse.lastExecutedNodeId,
        resumeToken: execResponse.resumeToken,
        schema,
        target,
        userInputs: null,
        workflow,
        workflowExecutionId,
        workflowNode,
      };

      return [
        ...actions,
        runtimeActions.storeRuntimeElementSchema({
          element: getWorkflowRuntimeElement(workflow.id),
          schema,
          isPaginated,
        }),
        runtimeActions.applyWorkflowNode(nextNodePayload),
      ];
    } else {
      this.logger.log(
        `Workflow ${workflow?.id} has been executed on the server. Next node: ${execResponse?.nextNodeId}`,
        {
          workflowExecutionId,
        },
      );
      const nextNode = getWorkflowNodeById(execResponse?.nextNodeId, workflow);
      // Show last executed node's success message if it has one
      if (execResponse?.outputMessages?.length) {
        // TODO[DK]: WF Errors: here we should validate wf execution effects
        const errorMessage = execResponse.outputMessages?.find(({ isSuccess }) => !isSuccess);
        const message =
          errorMessage || execResponse.outputMessages[execResponse.outputMessages.length - 1];
        if (message.isSuccess) {
          this.notificationService.success(
            message.externalMessage || message.internalMessage,
            true,
          );
        } else {
          const lastNode = workflow.nodes.find((n) => n.id === execResponse.lastExecutedNodeId);
          this.runtimeService.showWorkflowError({
            errorMessage: message.externalMessage || message.internalMessage,
            nodeId: execResponse.nextNodeId ?? lastNode?.nextNodeId,
            workflow,
          });
        }
      }
      // Server returning null or undefined means that no data was processed on the server. As such, we continue locally
      // with the original nodeInput data.
      // When outputName !== "$$input1" (e.g. $$currentRow from ForEach), the main pipeline input for the next node
      // is in outputs["$$input1"].data, not in the top-level data.
      if (!execResponse?.outputName || execResponse?.outputName === '$$input1') {
        nodeInput = isNullish(execResponse?.data) ? nodeInput : execResponse.data;
      } else {
        const input1Data = execResponse?.outputs?.['$$input1']?.data;
        nodeInput = isNullish(input1Data) ? nodeInput : input1Data;
      }

      if (isApiCallNode(workflowNode)) {
        const apiCallSchema: ApiCallSchema = {
          ...execSchema,
          id: workflow.id,
          name: workflowNode.actionName,
          supportsPagination: !!execSchema.supportsPagination,
        };
        if (execSchema.name === '$$input1') {
          execSchema.name = workflowNode.actionName;
        }
        this.schemaService.setApiCallSchema(workflowNode, apiCallSchema);
      }

      // Update context with variables stored during server-side execution
      this.variablesFacade.updateVariableInfosFromServer(execResponse.storedVariables);
      for (const storedVar of execResponse.variables || []) {
        const storedWfVar: WorkflowVariableInfo = {
          ...storedVar,
          name: storedVar.name,
        };
        ctx.storedVars[storedWfVar.name] = storedWfVar.value;
        ctx.storedVarsFullInfo[storedVar.name] = storedVar;
      }

      if (execResponse.storedVariables?.length) {
        this.variablesFacade.addVariableInfosFromServer(execResponse.storedVariables);
      }

      ctx = this.iss.updateRuntimeContextNamedOutput(ctx, nodeInput, execResponse.outputName);
      const nextNodePayload: ApplyWorkflowNodePayload = {
        affectedDatasetIds: execResponse.affectedDatasetIds,
        ctx,
        event,
        nodeInput,
        outputName: execResponse.outputName,
        prevNodeId: execResponse.lastExecutedNodeId,
        resumeToken: execResponse.resumeToken,
        schema: execSchema,
        target,
        userInputs: null,
        workflow,
        workflowExecutionId,
        workflowNode: nextNode,
      };

      return [
        ...actions,
        runtimeActions.storeRuntimeElementSchema({
          element: getWorkflowRuntimeElement(workflow.id),
          schema: execSchema,
          isPaginated,
        }),
        // We don't use applyNextWorkflowNode to make sure the node is executed on the frontend.
        runtimeActions.applyWorkflowNode(nextNodePayload),
      ];
    }
  }

  /**
   * Handles workflow execution error coming from the server.
   * @returns actions executing the workflow on the frontend
   */
  handleServerWorkflowExecutionError(errorInfo: ServerExecuteErrorData): Action[] {
    const { ctx, error, event, schema, target, workflow, workflowExecutionId, workflowNode } =
      errorInfo;
    this.logger.log(`Server execution errored out. Terminating workflow ${workflow?.id}.`, {
      extra: {
        error,
      },
      workflowExecutionId,
      level: 'error',
    });
    let nodeId = '';
    if (isHttpErrorResponse(error) && error.error.workflowNodeId) {
      nodeId = error.error.workflowNodeId;
    }

    this.runtimeService.showWorkflowError({ nodeId, serverError: error, workflow });

    const nextNodePayload: ApplyWorkflowNodePayload = {
      ctx,
      event,
      isWorkflowCancelled: true,
      nodeInput: [], // force [] since workflow errored out
      prevNodeId: workflowNode?.id,
      schema: schema || { ...DEFAULT_WF_SCHEMA, workflowId: workflow.id },
      target,
      userInputs: null,
      workflow,
      workflowExecutionId,
      workflowNode: null, // force null to terminate workflow
    };
    return [runtimeActions.applyWorkflowNode(nextNodePayload)];
  }

  mergeDialogDataWithInput(nodeInput: any, originalNodeInput: any): any {
    if (
      Array.isArray(originalNodeInput) &&
      !Array.isArray(nodeInput) &&
      typeof nodeInput === 'object'
    ) {
      nodeInput = originalNodeInput.map((row) => {
        return { ...row, ...nodeInput };
      });
    } else if (
      typeof originalNodeInput === 'object' &&
      !Array.isArray(nodeInput) &&
      typeof nodeInput === 'object'
    ) {
      nodeInput = { ...originalNodeInput, ...nodeInput };
    }
    return nodeInput;
  }

  /**
   * Determines whether given workflow should be entirely executed on the frontend side and not even send a request
   * to the backend.
   */
  shouldSkipServerExecution(workflow?: Workflow): boolean {
    let hasOnlyFrontendNodes = true;
    for (const node of workflow?.nodes || []) {
      hasOnlyFrontendNodes = hasOnlyFrontendNodes && isFrontendOnlyNode(node.nodeType, node);
    }
    if (RUN_LOCALLY || hasOnlyFrontendNodes) {
      return true;
    }
    return false;
  }

  showNodePromptOrContinue(
    workflow: Workflow,
    node: Nullish<WorkflowNode>,
    ctx: RuntimeContext,
    nodeInput: any,
    schema: WorkflowSchema,
    showDialogWhenFirstNode = false,
  ): Observable<NodePromptOutput> {
    if (
      !isNodeWithPrompt(node) ||
      (!hasPrompt(node) && !hasBulkEditPrompt(node)) ||
      (!showDialogWhenFirstNode && workflow.startNodeId === node.id)
    ) {
      return of({ nodeInput, dialogOutputData: null });
    }

    const originalNodeInput = deepClone(nodeInput);
    if (Array.isArray(nodeInput) && nodeInput.length) {
      nodeInput = nodeInput[0];
    }

    if (hasBulkEditPrompt(node)) {
      return this.showNodePromptForBulkEdit(node, nodeInput, originalNodeInput, workflow);
    }

    const assignments = this.createAssignmentsForDialog(node, nodeInput);
    ctx = this.iss.updateRuntimeContext(ctx, nodeInput);
    const dialogSchema = this.iss.getSchemaForDialog(
      schema,
      { assignments, id: node.id },
      nodeInput,
      ctx,
    );

    const dialogId = hasPrompt(node) ? node.dialogId : null;
    if (!dialogId) {
      return of({ nodeInput, dialogOutputData: null });
    }
    const page = syncSelect(this.store, runtimeSelectors.selectPageById(dialogId));
    return this.dialogActionService
      .dialogOpen({
        assignments,
        ctx,
        dialogId,
        nodeInput,
        nodeType: node.nodeType,
        passDataAsInput: true,
        schema: dialogSchema,
        isDrawer: page?.pageType === 'Drawer',
      })
      .pipe(
        map((dialogResult) => {
          if (dialogResult?.payload) {
            // If the original input was an array - combine the data from the array with values from the dialog (BL-3688)
            let payload = dialogResult.payload;
            if (Array.isArray(originalNodeInput) && originalNodeInput.length) {
              // Extract assigned columns (so the unassigned ones don't override data from original input)
              payload = dialogSchema.columns.reduce(
                (acc, { name }) => {
                  acc[name] = payload[name];
                  return acc;
                },
                {} as Record<string, unknown>,
              );

              // Create combined payload array
              payload = originalNodeInput.map((row) => ({ ...row, ...payload }));
            }
            const userInputs = Array.isArray(payload) ? payload[0] : payload;
            return {
              dialogOutputData: userInputs ?? null,
              nodeInput: payload,
            };
          }
          this.logger.log(`Workflow ${workflow.id} canceled with a dialog`, {
            workflowId: workflow.id,
          });
          return { cancelledByDialog: true, dialogOutputData: null, nodeInput: originalNodeInput };
        }),
      );
  }

  throwVariableExistsInDifferentScopeError(storedVar: VariableInfo): never {
    const errorMessage = `${RuntimeErrorMessage.VariableExistsInDifferentScope}`
      .replaceAll('{{VAR_NAME}}', storedVar.name)
      .replaceAll('{{VAR_SCOPE}}', storedVar.scope);
    throw new Error(errorMessage);
  }

  throwInvalidNodeError(node: WorkflowNode | null, missingProperties?: string): never {
    throwInvalidNodeError(node, missingProperties);
  }

  throwInsufficientInputDataError(node: WorkflowNode | null, data: any): never {
    console.log('Connection aborted. Insufficient input data:', node, data);
    throw new Error(`${RuntimeErrorMessage.InsufficientInputData}`);
  }

  throwInvalidInputDataError(node: WorkflowNode | null, data: any, msg: string): never {
    console.log('Connection aborted. Invalid input data:', node, data);
    console.log(`${msg}`);
    throw new Error(`${RuntimeErrorMessage.InvalidInputData}`);
  }

  throwUnsupportedFrontendNode(node: WorkflowNode | null): never {
    const nodeType = node ? `${node.nodeType.toUpperCase()} (${node?.id})` : 'unknown node';
    const errorMessage = `${RuntimeErrorMessage.UnsupportedFrontendNode}`.replaceAll(
      '{{NODE_NAME}}',
      nodeType,
    );
    console.log(errorMessage, node);
    throw new Error(errorMessage);
  }

  validateNextNode(node: WorkflowNode, nextNode: WorkflowNode | null): void | never {
    assertNextNodeIsNotSelf(node, nextNode);
  }

  wasCancelledByDialog(promptOutput: NodePromptOutput): boolean {
    return (
      promptOutput !== null &&
      typeof promptOutput === 'object' &&
      'cancelledByDialog' in promptOutput &&
      !!promptOutput.cancelledByDialog
    );
  }

  private createAssignmentsForDialog(node: WorkflowNode, nodeInput: any): DataAssignment[] {
    if (!isNodeWithPrompt(node)) {
      return [];
    }
    let assignments = node.dialogAssignments;
    if (!nodeInput && isInsertNode(node) && node.datasetId) {
      const datasetSchema = this.schemaService.getSchema(node.datasetId);
      if (datasetSchema) {
        assignments = produce(assignments, (draft) => {
          nodeInput = datasetSchema.columns.reduce(
            (result, field) => {
              if (!isNullish(field.defaultValue)) {
                result[field.name] = field.defaultValue;
                const assignment = findColumn(draft, { name: field.id ?? field.name });
                if (assignment && !assignment.value) {
                  assignment.value = `$$input1["${field.id ?? field.name}"]`;
                }
              }
              return result;
            },
            {} as Record<string, any>,
          );
        });
      }
    }
    return assignments ?? [];
  }

  private findGlobalQueryForWorkflow(
    workflow: Workflow,
    globalQueries: Dictionary<GlobalQueryState>,
  ): GlobalQueryState | undefined {
    const firstNode = findFirstNodeInWorkflow(workflow);
    if (isExecuteWorkflowNode(firstNode)) {
      const innerWorkflow = syncSelect(
        this.store,
        runtimeSelectors.selectWorkflowById(firstNode.workflowId),
      );
      if (innerWorkflow) {
        return this.findGlobalQueryForWorkflow(innerWorkflow, globalQueries);
      }
      return undefined;
    }
    if (!isGetNode(firstNode)) {
      return;
    }
    for (const datasetId in globalQueries || {}) {
      if (firstNode.datasetId === datasetId) {
        return globalQueries[datasetId];
      }
    }
    return undefined;
  }
  // first nodes in execution chain that are IF or ASSIGN are considered "config-nodes"
  private getFirstFunctionalNode(workflow: Workflow): WorkflowNode | undefined {
    let node = findFirstNodeInWorkflow(workflow);

    while (isIfNode(node) || isAssignNode(node)) {
      node = workflow.nodes.find((n) => n.id === node?.nextNodeId);
    }

    return node;
  }

  private getGroupsForElement(element: CanvasElement | undefined): (ColumnId | string)[] | null {
    if (isElementType(element, 'table')) {
      return (
        element.props.content.table?.groupBy?.map((group) => group.field).filter(Boolean) || null
      );
    }
    return null;
  }

  /**
   * Checks whether given workflow has all the required inputs to be started.
   */
  private getMissingDataInputs(
    event: RuntimeEvent,
    workflow: Workflow | null,
    ctx: RuntimeContext,
    fileButtonInputName = '',
  ): string[] {
    if (workflow?.allowNullInput) {
      return [];
    }
    let missingInputs: string[] = [];
    const missingConditionValues: string[] = [];
    // Incoming event is not a click. Iterate through all inputs connected to the workflow and check with existing
    // context whether we have all the necessary data to run the workflow. If one of the  inputs to the workflow is
    // a button, it will implicitly be undefined and the workflow won't run. The assumption here is that if a button
    // is connected to the workflow, it needs to be pressed in order for the workflow to run.
    if (workflow && event?.connection?.eventType !== 'click') {
      for (const inputName in workflow.inputs) {
        if (inputName === '') {
          this.logger.log('Invalid (not defined) input name in', {
            extra: {
              inputs: workflow.inputs,
            },
            level: 'error',
            workflowId: workflow.id,
          });
          throw new Error(
            `${RuntimeErrorMessage.InvalidInputName} (${inputName}) for one of the inputs`,
          );
        }

        let isInputUsedInNonOptionalAssignment = false;
        let isInputUsedInNonOptionalCondition = false;
        const isInputUsed = this.workflowReferenceService.usesInput(workflow, `$$${inputName}`);
        const inputUsedInFirstNode =
          this.workflowReferenceService.firstNodeUsesInput(workflow, `$$${inputName}`) ||
          (fileButtonInputName
            ? this.workflowReferenceService.firstNodeUsesInput(workflow, `$$${fileButtonInputName}`)
            : false);

        if (Object.prototype.hasOwnProperty.call(workflow.inputs, inputName)) {
          // Check whether the value inside the context value (for example, _id inside {_id: '123' }) is either
          // a defined if it's not. If not, take note of that fact.
          let requiredContextValuesDefined = true;
          const firstNode = this.getFirstFunctionalNode(workflow);

          // Check whether conditions have all the required values.
          let allConditionsOptional = true;
          let conditions: WorkflowPredicate[] | ComparisonQueryNode[] = [];
          if (hasLogicalQueryNode(firstNode) && firstNode.query?.children?.length) {
            conditions = getAllComparisonNodes(firstNode.query);
          } else if (hasQueryConditions(firstNode) && firstNode?.queryConditions?.length) {
            conditions = firstNode.queryConditions;
          }
          for (const condition of conditions || []) {
            allConditionsOptional = allConditionsOptional && !!condition.isOptional;
            // Check individual value of the condition inside the context
            if (!condition.isOptional && condition.value) {
              // Check whether given input (inputName) is used in an non-optional condition. If it is used,
              // that input when evaluated must have a value. If it doesn't, the workflow should be blocked.
              isInputUsedInNonOptionalCondition ||= condition.value.includes(inputName);
              const isJsonPathValue = condition.value.includes('..');
              let evaluatedValue;
              if (isJsonPathValue) {
                const valueToCheck = condition.value.split('..')[0];
                evaluatedValue = this.iss.eval(valueToCheck, ctx);
              } else if (isTagId(condition.value)) {
                evaluatedValue = condition.value;
              } else {
                evaluatedValue = this.iss.eval(condition.value, ctx);
              }
              if (
                typeof evaluatedValue === 'undefined' ||
                evaluatedValue === null ||
                (isJsonPathValue && Array.isArray(evaluatedValue) && evaluatedValue.length === 0) ||
                (condition?.operator === ComparisonOperator.Near &&
                  !areAllValuesDefined(evaluatedValue))
              ) {
                missingConditionValues.push(condition.value);
                requiredContextValuesDefined = false;
              }
            }
          }

          // Check whether any of the API call assignments is not optional (when it doesn't show a dialog)
          let assignments: DataAssignment[] = [];
          let dialogAssignments: DataAssignment[] = [];
          if (isApiCallNode(firstNode)) {
            assignments = firstNode.assignments || [];
            dialogAssignments = firstNode.dialogAssignments || [];
          }
          for (const assignment of assignments) {
            // Check whether assignment has a value only when it's required and is not a formula
            // We assume that if it's a formula it will have a value. If it's a dialog assignment, we allow it even
            // if it has no value.
            const isDialogAssignment = !!dialogAssignments.find(
              ({ name }) => name === assignment.name,
            );
            if (
              !assignment.isOptional &&
              assignment.value &&
              !assignment.isFormulaValue &&
              !isDialogAssignment
            ) {
              isInputUsedInNonOptionalAssignment = assignment.value.includes(inputName);
              const evaluatedValue = this.iss.eval(assignment.value, ctx);
              if (typeof evaluatedValue === 'undefined' || evaluatedValue === null) {
                missingConditionValues.push(assignment.value);
                requiredContextValuesDefined = false;
              }
            }
          }

          // file upload input can't be ignored.
          if (isInputUsed && inputName === fileButtonInputName) {
            isInputUsedInNonOptionalCondition = true;
          }

          // Block if the first node requires file upload but no file has been uploaded
          if (
            (inputName === MAIN_INPUT_NAME &&
              inputUsedInFirstNode &&
              ctx[inputName] === undefined) ||
            (inputName !== MAIN_INPUT_NAME &&
              isInputUsed &&
              ctx[inputName] === undefined &&
              isInputUsedInNonOptionalCondition) ||
            (inputName !== MAIN_INPUT_NAME &&
              isInputUsed &&
              !requiredContextValuesDefined &&
              isInputUsedInNonOptionalAssignment)
          ) {
            missingInputs.push(inputName);
          }
          if (!requiredContextValuesDefined && !allConditionsOptional) {
            // Input has to either have all context values defined or have the required ones be
            missingInputs = missingInputs.concat(missingConditionValues);
          }
        }
      }
    }
    // Incoming event is a click. Iterate through all inputs excluding the button input, and check with existing
    // context whether we have all the necessary data to run the workflow. We ignore button input because we know
    // a button has been pressed (and since it carries no data we can ignore it).
    else if (workflow && event?.connection?.eventType === 'click') {
      for (const inputName in workflow.inputs) {
        const isInputUsed = this.workflowReferenceService.usesInput(workflow, `$$${inputName}`);
        if (isInputUsed && Object.prototype.hasOwnProperty.call(workflow.inputs, inputName)) {
          if (event.connection.id !== workflow.inputs[inputName] && ctx[inputName] === undefined) {
            const inputUsedInFirstNode = this.workflowReferenceService.firstNodeUsesInput(
              workflow,
              `$$${inputName}`,
            );
            if (
              inputName !== MAIN_INPUT_NAME ||
              (inputName === MAIN_INPUT_NAME && inputUsedInFirstNode)
            ) {
              this.logger.log(`required data from ${inputName} not found.`, {
                workflowId: workflow.id,
              });
              missingInputs.push(inputName);
            }
          }
        }
      }
    }
    return Array.from(new Set(missingInputs || []));
  }

  /**
   * Returns next connection in sequence based on the previous connection. Source of new connection is
   * the target of previous connection.
   */
  private getNextConnection(prevConnection: Connection): Connection | null {
    return this.runtimeFacade.getConnectionBySource(prevConnection.target);
  }

  private getWorkflowInputs(workflow: Workflow | undefined): WorkflowInputs | undefined {
    if (!workflow) {
      return undefined;
    }
    if (!isBulkActionWorkflow(workflow)) {
      return workflow.inputs;
    }

    const effectiveWorkflowId = getEffectiveWorkflowId(workflow);
    if (!effectiveWorkflowId) {
      return;
    }
    const effectiveWorkflow = syncSelect(
      this.store,
      runtimeSelectors.selectWorkflowById(effectiveWorkflowId),
    );
    return effectiveWorkflow?.inputs;
  }

  private findMissingExternalVars(workflow: Workflow | null, ctx: RuntimeContext): string[] {
    return ['$$Account', '$$User', '$$Page'].filter(
      (key) =>
        this.workflowReferenceService.usesInput(workflow, key) &&
        !Object.keys(ctx?.[key] || {})?.length,
    );
  }

  /**
   * Merges element query (from filter builder) with global data filter query.
   * @param elementQuery
   * @param globalQuery
   * @returns
   */
  private mergeQueries(
    elementQuery: LogicalQueryNode | undefined,
    globalQuery: LogicalQueryNode | undefined,
  ): LogicalQueryNode | null {
    let query: LogicalQueryNode | undefined = elementQuery;
    if (globalQuery) {
      const children = deepClone(globalQuery.children || []);
      if (query) {
        children.push(deepClone(query));
      }
      query = { ...globalQuery, children };
    }
    return query ?? null;
  }

  private showNodePromptForBulkEdit(
    node: WorkflowNode,
    nodeInput: any,
    originalNodeInput: any,
    workflow: Workflow,
  ): Observable<NodePromptOutput> {
    if (!hasBulkEditPrompt(node)) {
      return of({ nodeInput, dialogOutputData: null });
    }

    if (!node.datasetId) {
      return of({ nodeInput, dialogOutputData: null });
    }
    const datasetSchema = this.schemaService.getSchema(node.datasetId);
    if (!datasetSchema) {
      throw new Error('unable to obtain schema');
    }
    const columns = node.chooseColumns
      ? datasetSchema.columns.filter((column) => node.columns?.includes(getColumnKey(column)))
      : datasetSchema.columns;
    return this.bulkEditService.open(columns).pipe(
      map((dialogResult) => {
        if (dialogResult?.payload) {
          const idColName = datasetSchema?.columns.find((col) => col.isId)?.name;
          let payload = Array.isArray(dialogResult.payload)
            ? dialogResult.payload[0]
            : dialogResult.payload;
          if (Array.isArray(originalNodeInput) && originalNodeInput.length && idColName) {
            payload = originalNodeInput.map((input) => ({
              [idColName]: input[idColName],
              ...payload,
            }));
          } else if (idColName && !Array.isArray(originalNodeInput)) {
            payload[idColName] = originalNodeInput[idColName];
          }
          return { nodeInput: payload, dialogOutputData: dialogResult.payload };
        }
        this.logger.log(`Workflow ${workflow.id} canceled with a dialog`, {
          workflowId: workflow.id,
        });
        return { cancelledByDialog: true, nodeInput: undefined, dialogOutputData: undefined };
      }),
    );
  }
}

function shouldBlockWizardEventWorkflow(
  workflowId: string | undefined,
  uiEntities: Dictionary<CanvasElement>,
  connections: Connection[],
  eventConnection: Connection,
  isAutostart: boolean,
  isDataRefresh: boolean,
): boolean {
  const isWizardConnection = (c: Connection): boolean =>
    c.target.layerType === 'Workflow' &&
    c.target.id === workflowId &&
    c.source.layerType === 'Ui' &&
    uiEntities[c.source.id]?.type === 'wizard' &&
    c.eventType === 'submit';
  const isWizardSubmitWorkflow = !!connections.find(isWizardConnection);
  const isWizardSubmitEvent = isWizardConnection(eventConnection) && !eventConnection.autostart;

  const isWizardNextConnection = (c: Connection): boolean =>
    c.target.layerType === 'Workflow' &&
    c.target.id === workflowId &&
    c.source.layerType === 'Ui' &&
    uiEntities[c.source.id]?.type === 'wizard' &&
    c.eventType === 'next';
  const isWizardNextWorkflow = !!connections.find(isWizardNextConnection);
  const isWizardNextEvent = isWizardNextConnection(eventConnection) && !eventConnection.autostart;

  const isWizardWorkflow =
    (isWizardSubmitWorkflow && !isWizardSubmitEvent) || // Wizard submit workflow & not a submit event -> block
    (isWizardNextWorkflow && !isWizardNextEvent) || // Wizard next workflow & not a next event -> block
    (isWizardSubmitWorkflow && isWizardSubmitEvent && (isAutostart || isDataRefresh)); // Wizard submit workflow & submit event & autostart/data refresh -> block
  return isWizardWorkflow;
}

function getGroupDataRowCount(group: Record<string, any>): number {
  return (
    (group.data?.length ?? 0) +
    (group.groupedData?.reduce((acc: number, g: Record<string, any>) => {
      acc += getGroupDataRowCount(g);
      return acc;
    }, 0) ?? 0)
  );
}
